import {
  Button,
  DatePicker,
  Form,
  Image,
  Input,
  message,
  Select,
  Table,
  Tag,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import {
  GetOrderListV2Res,
  getOrderListV2,
  getOrderStatusStatistics,
} from 'apis/oms';
import usePagination from 'commons/hooks/usePagination';
import LOCALS from 'commons/locals';
import { OrderDeliveryTypeOptionList, findLabelByValue } from 'commons/options';
import { ORDER_PAYMENT_STATUS_MAP } from 'constant/oms-order-payment-status';
import LinkButton from 'components/link-button';
import showTotal from 'components/show-total';
import dayjs from 'dayjs';
import i18n from 'i18n';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from 'store/hooks';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import { selectUserInfo } from 'store/slices/userInfoSlice';
import formatTime from 'utils/formatTime';
import classNames from 'classnames';
import { CURRENCY_ENUM } from 'types/pms';
import * as XLSX from 'xlsx';
import { OMS_ORDER_TAG_OPTION_LIST } from 'constant';
import { priceToWithoutTax } from 'utils/price-change';
import { financialManagementAccountList } from 'apis/fms';
import {
  AlertCircle,
  CheckCircle,
  Clock,
  Package,
  XCircle,
} from 'lucide-react';
import { Card } from 'antd/lib';
import {
  parseAsArrayOf,
  parseAsBoolean,
  parseAsInteger,
  parseAsString,
  useQueryState,
} from 'nuqs';
import { getStockPlaceConfig, isStockPlace } from 'constant/stock-place';
import { CURRENCY_OPTION_LIST } from 'constant/currency';
import {
  getOrderCreateSourceConfig,
  ORDER_CREATED_FROM_OPTION_LIST,
} from 'constant/order-create-source';
import { PRINTABLE_INVOICE_STOCK_PLACE_LIST } from 'constant/oms-order';
import { getOrderStatusConfig } from 'constant/oms-order-status';
import { ORDER_STATUS_MAP } from 'constant/oms-order-status';
import useResource from 'commons/hooks/useResource';

const useOrderListQueryParams = () => {
  const [pageNum, setPageNum] = useQueryState(
    'pageNum',
    parseAsInteger.withDefault(1),
  );
  const [pageSize, setPageSize] = useQueryState(
    'pageSize',
    parseAsInteger.withDefault(10),
  );
  const [orderSn, setOrderSn] = useQueryState('orderSn');
  const [productSn, setProductSn] = useQueryState('productSn');
  const [currencyList, setCurrencyList] = useQueryState(
    'currencyList',
    parseAsArrayOf(parseAsString),
  );
  const [createdFroms, setCreatedFroms] = useQueryState(
    'createdFroms',
    parseAsArrayOf(parseAsInteger),
  );
  const [deliveryType, setDeliveryType] = useQueryState(
    'deliveryType',
    parseAsString,
  );
  const [tag, setTag] = useQueryState('tag', parseAsArrayOf(parseAsString));
  const [statuses, setStatuses] = useQueryState(
    'statuses',
    parseAsArrayOf(parseAsInteger),
  );
  const [createTime, setCreateTime] = useQueryState(
    'createTime',
    parseAsArrayOf(parseAsString).withDefault([]),
  );
  const [staffName, setStaffName] = useQueryState('staffName');
  const [freightGtZero, setFreightGtZero] = useQueryState(
    'freightGtZero',
    parseAsBoolean,
  );
  const [note, setNote] = useQueryState('note');

  return {
    pageNum,
    pageSize,
    orderSn,
    productSn,
    currencyList,
    createdFroms,
    deliveryType,
    tag,
    statuses,
    createTime,
    staffName,
    freightGtZero,
    note,
    setOrderSn,
    setProductSn,
    setCurrencyList,
    setPageNum,
    setPageSize,
    setCreatedFroms,
    setDeliveryType,
    setTag,
    setStatuses,
    setCreateTime,
    setStaffName,
    setFreightGtZero,
    setNote,
  };
};

const isPrintableInvoiceOrder = (order: GetOrderListV2Res['list'][number]) => {
  if (!order.deliveryTime || !order.orderItems.length) return false;

  const stockPlaceList = order.orderItems.map(
    (item) => item.product.stockPlace,
  );
  const isAllProductStockPlaceSame = stockPlaceList.every(
    (stockPlace) => stockPlace === stockPlaceList[0],
  );

  if (!isAllProductStockPlaceSame) return false;

  return PRINTABLE_INVOICE_STOCK_PLACE_LIST.includes(
    stockPlaceList[0] as (typeof PRINTABLE_INVOICE_STOCK_PLACE_LIST)[number],
  );
};

const OrderList = () => {
  const { RangePicker } = DatePicker;
  const navigate = useNavigate();
  const { orderTypeOptions, staffSelectOptions } =
    useAppSelector(selectGlobalInfo);
  const canFilterByStaffName = useResource('order-list-filter-staff-name');
  const { isLoading: isUserInfoLoading } = useAppSelector(selectUserInfo);
  const [orderStatusStatistics, setOrderStatusStatistics] = useState<
    {
      count: string;
      status: number;
    }[]
  >([]);
  const [selectedOrderIds, setSelectedOrderIds] = useState<React.Key[]>([]);
  const [selectedOrders, setSelectedOrders] = useState<
    GetOrderListV2Res['list'][number][]
  >([]);

  const {
    pageNum,
    pageSize,
    setPageNum,
    setPageSize,
    orderSn,
    setOrderSn,
    productSn,
    setProductSn,
    currencyList,
    setCurrencyList,
    createdFroms,
    setCreatedFroms,
    deliveryType,
    setDeliveryType,
    tag,
    setTag,
    statuses,
    setStatuses,
    createTime,
    setCreateTime,
    staffName,
    setStaffName,
    freightGtZero,
    setFreightGtZero,
    note,
    setNote,
  } = useOrderListQueryParams();

  // 权限加载前后，实际担当者条件可能相同。依赖解析后的值，进页面只请求一次。
  const requestStaffName =
    canFilterByStaffName && staffName ? staffName : undefined;
  const requestNote = note?.trim() || undefined;

  // 文本条件先记在输入框里，点搜索后才写入 URL 并请求。
  const [orderSnInput, setOrderSnInput] = useState(orderSn ?? '');
  const [productSnInput, setProductSnInput] = useState(productSn ?? '');
  const [noteInput, setNoteInput] = useState(note ?? '');

  useEffect(() => {
    setOrderSnInput(orderSn ?? '');
  }, [orderSn]);

  useEffect(() => {
    setProductSnInput(productSn ?? '');
  }, [productSn]);

  useEffect(() => {
    setNoteInput(note ?? '');
  }, [note]);

  const normalizeTextQuery = (value: string) => {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  };

  const handleTextSearch = () => {
    setOrderSn(normalizeTextQuery(orderSnInput));
    setProductSn(normalizeTextQuery(productSnInput));
    setNote(normalizeTextQuery(noteInput));
    setPageNum(1);
  };

  const staffNameOptions = useMemo(
    () =>
      staffSelectOptions
        .filter((i) => i.status === 1)
        .map((i) => ({
          value: i.label,
          label: i.label,
        })),
    [staffSelectOptions],
  );

  const statisticsFilters = useMemo(
    () => ({
      orderSn: orderSn || undefined,
      productSn: productSn || undefined,
      currencyList: currencyList || undefined,
      createdFroms: createdFroms || undefined,
      deliveryType: deliveryType || undefined,
      tag: tag || undefined,
      start: createTime[0] || undefined,
      end: createTime[1] || undefined,
      staffName: requestStaffName,
      freightGtZero: freightGtZero || undefined,
      note: requestNote,
    }),
    [
      orderSn,
      productSn,
      currencyList,
      createdFroms,
      deliveryType,
      tag,
      createTime,
      requestStaffName,
      freightGtZero,
      requestNote,
    ],
  );

  useEffect(() => {
    if (isUserInfoLoading) return;

    getOrderStatusStatistics(statisticsFilters).then((res) => {
      setOrderStatusStatistics(res);
    });
  }, [isUserInfoLoading, statisticsFilters]);

  const { loading, setLoading, total, setTotal, dataSource, setDataSource } =
    usePagination<GetOrderListV2Res['list'][number]>();
  const { orderStatusOptions } = useAppSelector(selectGlobalInfo);

  const getDataSource = useCallback(
    async (customPageNum?: number, customPageSize?: number) => {
      try {
        setLoading(true);
        const { list, total } = await getOrderListV2({
          pageNum: customPageNum || pageNum,
          pageSize: customPageSize || pageSize,
          orderSn: orderSn || undefined,
          productSn: productSn || undefined,
          currencyList: currencyList || undefined,
          createdFroms: createdFroms || undefined,
          deliveryType: deliveryType || undefined,
          tag: tag || undefined,
          statuses: statuses || undefined,
          start: createTime[0] || undefined,
          end: createTime[1] || undefined,
          staffName: requestStaffName,
          freightGtZero: freightGtZero || undefined,
          note: requestNote,
        });
        setDataSource(list);
        setTotal(total);
        return { list, total };
      } catch (err) {
      } finally {
        setLoading(false);
      }
    },
    [
      pageNum,
      pageSize,
      orderSn,
      productSn,
      currencyList,
      createdFroms,
      deliveryType,
      tag,
      statuses,
      createTime,
      requestStaffName,
      freightGtZero,
      requestNote,
      setLoading,
      setDataSource,
      setTotal,
    ],
  );

  useEffect(() => {
    if (isUserInfoLoading) return;

    getDataSource();
  }, [isUserInfoLoading, getDataSource]);

  const columns: ColumnsType<GetOrderListV2Res['list'][number]> =
    useMemo(() => {
      return [
        {
          title: <Trans i18nKey={LOCALS.dwawuQNUEi}></Trans>,
          dataIndex: 'id',
          fixed: 'left',
          key: 'id',
          render: (id: GetOrderListV2Res['list'][number]['id']) => {
            return <LinkButton href={`/oms/order-view/${id}`}>{id}</LinkButton>;
          },
        },
        {
          title: <Trans i18nKey={LOCALS.order_sn}></Trans>,
          dataIndex: 'orderSn',
          key: 'orderSn',
          render: (
            orderSn: GetOrderListV2Res['list'][number]['orderSn'],
            record: GetOrderListV2Res['list'][number],
          ) => {
            return (
              <LinkButton href={`/oms/order-view/${record.id}`}>
                {orderSn}
              </LinkButton>
            );
          },
        },
        {
          title: <Trans i18nKey={LOCALS.product_pictures}></Trans>,
          key: 'product_pictures',
          render: ({ orderItems }: GetOrderListV2Res['list'][number]) => {
            const imageList = orderItems
              .map(({ productPic }) => productPic)
              .filter((i) => i);
            if (imageList.length === 0) return '-';

            return (
              <Image.PreviewGroup items={[...imageList]}>
                <Image
                  width={64}
                  height={64}
                  className="object-cover"
                  src={imageList[0]}
                />
              </Image.PreviewGroup>
            );
          },
        },
        {
          title: <Trans i18nKey={LOCALS.sales_product_id}></Trans>,
          dataIndex: 'orderItems',
          key: 'order-id-list',
          render: (
            orderItems: GetOrderListV2Res['list'][number]['orderItems'],
          ) => {
            return (
              <div>
                {orderItems.map(({ productId }) => {
                  const href = `/pms/product-view/${productId}`;
                  return (
                    <div key={productId}>
                      <a
                        href={href}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(href);
                        }}
                      >
                        {productId}
                      </a>
                    </div>
                  );
                })}
              </div>
            );
          },
        },
        {
          title: <Trans i18nKey={LOCALS.SYLYoWTeQq}></Trans>,
          dataIndex: 'orderItems',
          key: 'orderItems',
          render: (
            orderItems: GetOrderListV2Res['list'][number]['orderItems'],
          ) => {
            return (
              <div>
                {orderItems.map(({ product, productId }) => {
                  const href = `/pms/product-view/${productId}`;
                  return (
                    <div key={productId}>
                      {product.description ? (
                        <a
                          href={href}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(href);
                          }}
                        >
                          {product.description}
                        </a>
                      ) : (
                        '-'
                      )}
                    </div>
                  );
                })}
              </div>
            );
          },
        },
        {
          title: <Trans i18nKey={LOCALS.stock_place} />,
          key: 'stockPlace',
          render: ({ orderItems }: GetOrderListV2Res['list'][number]) => {
            if (!orderItems.length) return '-';
            const { stockPlace } = orderItems[0].product;

            if (!isStockPlace(stockPlace)) return '-';

            return getStockPlaceConfig(stockPlace).label;
          },
        },
        {
          title: <Trans i18nKey={LOCALS.email} />,
          key: 'memberUsername',
          render: ({
            memberUsername,
            memberId,
          }: GetOrderListV2Res['list'][number]) => {
            if (!memberId) return '-';

            return (
              <LinkButton href={`/ums/member-view/${memberId}`}>
                {memberUsername}
              </LinkButton>
            );
          },
        },
        {
          title: <Trans i18nKey={LOCALS.order_type} />,
          dataIndex: 'orderType',
          key: 'orderType',
          render: (
            orderType: GetOrderListV2Res['list'][number]['orderType'],
          ) => {
            return findLabelByValue(orderType, orderTypeOptions);
          },
        },
        {
          title: <Trans i18nKey={LOCALS.pay_amount}></Trans>,
          key: 'payAmount',
          render: ({
            payAmount,
            payAmountActualCurrency,
            orderItems: [{ actualCurrency }],
          }: GetOrderListV2Res['list'][number]) => {
            if (actualCurrency && payAmountActualCurrency !== null) {
              return `${actualCurrency} ${payAmountActualCurrency.toLocaleString()}`;
            }

            return `JPY ${payAmount.toLocaleString()}`;
          },
        },
        {
          title: <Trans i18nKey={LOCALS.created_from}></Trans>,
          dataIndex: 'createdFrom',
          key: 'createdFrom',
          render: (
            createdFrom: GetOrderListV2Res['list'][number]['createdFrom'],
          ) => {
            return findLabelByValue(
              createdFrom,
              ORDER_CREATED_FROM_OPTION_LIST,
            );
          },
        },
        {
          title: <Trans i18nKey={LOCALS.staff} />,
          dataIndex: 'staffName',
          key: 'staffName',
          render: (value: GetOrderListV2Res['list'][number]['staffName']) =>
            value || '-',
        },
        {
          title: <Trans i18nKey={LOCALS.vucsrXTzfQ}></Trans>,
          dataIndex: 'deliveryType',
          key: 'deliveryType',
          render: (
            deliveryType: GetOrderListV2Res['list'][number]['deliveryType'],
          ) => {
            return findLabelByValue(deliveryType, OrderDeliveryTypeOptionList);
          },
        },
        {
          title: <Trans i18nKey={LOCALS.khvsAbbLJs} />,
          key: 'tagList',
          dataIndex: 'tagList',
          render: (tagList: GetOrderListV2Res['list'][number]['tagList']) => {
            if (!tagList || tagList.length === 0) return '-';

            return (
              <div className="flex flex-wrap gap-1">
                {tagList.map(({ tag, id }) => {
                  return (
                    <Tag
                      color={
                        OMS_ORDER_TAG_OPTION_LIST.find((i) => i.value === tag)
                          ?.color || ''
                      }
                      key={id}
                    >
                      {tag}
                    </Tag>
                  );
                })}
              </div>
            );
          },
        },
        {
          title: <Trans i18nKey={LOCALS.created_time}></Trans>,
          dataIndex: 'createTime',
          key: 'createTime',
          render: (
            createTime: GetOrderListV2Res['list'][number]['createTime'],
          ) => {
            return formatTime(createTime);
          },
        },
        {
          title: <Trans i18nKey={LOCALS.delivery_time}></Trans>,
          dataIndex: 'deliveryTime',
          key: 'deliveryTime',
          render: (
            deliveryTime: GetOrderListV2Res['list'][number]['deliveryTime'],
          ) => {
            return formatTime(deliveryTime);
          },
        },
        {
          title: <Trans i18nKey={LOCALS.MultiplePayList}></Trans>,
          dataIndex: 'multiplePayStatus',
          key: 'multiplePayStatus',
          render: (
            value: GetOrderListV2Res['list'][number]['multiplePayStatus'],
          ) => {
            return value ? <Trans i18nKey={LOCALS.yes} /> : '-';
          },
        },
        {
          title: <Trans i18nKey={LOCALS.order_status}></Trans>,
          dataIndex: 'status',
          key: 'status',
          fixed: 'right',
          render: (status: GetOrderListV2Res['list'][number]['status']) => {
            return (
              <Tag color={getOrderStatusConfig(status).color}>
                {findLabelByValue(status, orderStatusOptions)}
              </Tag>
            );
          },
        },
      ];
    }, [navigate, orderStatusOptions, orderTypeOptions]);

  const selectedPrintableOrderIds = useMemo(
    () =>
      selectedOrders.filter(isPrintableInvoiceOrder).map((order) => order.id),
    [selectedOrders],
  );

  return (
    <div className="space-y-4">
      <Card bodyStyle={{ padding: '16px' }}>
        <Form layout={'inline'} onFinish={handleTextSearch}>
          <Form.Item label={<Trans i18nKey={LOCALS.KaLvBciebv} />}>
            <Input.TextArea
              value={orderSnInput}
              onChange={(e) => {
                setOrderSnInput(e.target.value);
              }}
              placeholder={i18n.t(LOCALS.please_enter) || ''}
            />
          </Form.Item>

          <Form.Item label={<Trans i18nKey={LOCALS.jHkqqeaSOv} />}>
            <Input.TextArea
              value={productSnInput}
              onChange={(e) => {
                setProductSnInput(e.target.value);
              }}
              placeholder={i18n.t(LOCALS.please_enter) || ''}
            />
          </Form.Item>

          <Form.Item label={<Trans i18nKey={LOCALS.created_time} />}>
            <RangePicker
              value={
                createTime.length === 2
                  ? [dayjs(createTime[0]), dayjs(createTime[1])]
                  : null
              }
              onChange={(dates) => {
                if (dates) {
                  setCreateTime([
                    dayjs(dates[0]).startOf('days').format() || '',
                    dayjs(dates[1]).endOf('days').format() || '',
                  ]);
                } else {
                  setCreateTime(null);
                }

                setPageNum(1);
              }}
            />
          </Form.Item>

          <Form.Item label={<Trans i18nKey={LOCALS.currency} />}>
            <Select
              value={currencyList || undefined}
              onChange={(value) => {
                if (!value.length) {
                  setCurrencyList(null);
                } else {
                  setCurrencyList(value);
                }
                setPageNum(1);
              }}
              mode="multiple"
              style={{ minWidth: 150 }}
              placeholder={i18n.t(LOCALS.please_select)}
              options={CURRENCY_OPTION_LIST}
            />
          </Form.Item>

          <Form.Item label={<Trans i18nKey={LOCALS.created_from} />}>
            {/* TODO: 不同店铺账号之间数据权限，只能查看自己店铺的订单 */}
            <Select
              value={createdFroms || undefined}
              onChange={(value) => {
                if (!value.length) {
                  setCreatedFroms(null);
                } else {
                  setCreatedFroms(value);
                }
                setPageNum(1);
              }}
              mode="multiple"
              style={{ minWidth: 150 }}
              placeholder={i18n.t(LOCALS.please_select)}
              options={ORDER_CREATED_FROM_OPTION_LIST}
            />
          </Form.Item>

          <Form.Item name="statuses" initialValue={[]} hidden>
            <Select
              mode="multiple"
              allowClear
              style={{ width: 150 }}
              options={orderStatusOptions}
            />
          </Form.Item>

          <Form.Item label={<Trans i18nKey={LOCALS.vucsrXTzfQ}></Trans>}>
            <Select
              value={deliveryType || undefined}
              onChange={(value) => {
                if (!value) {
                  setDeliveryType(null);
                } else {
                  setDeliveryType(value);
                }
                setPageNum(1);
              }}
              allowClear
              placeholder={i18n.t(LOCALS.please_select)}
              options={OrderDeliveryTypeOptionList}
              style={{ width: 150 }}
            />
          </Form.Item>

          <Form.Item label={<Trans i18nKey={LOCALS.khvsAbbLJs}></Trans>}>
            <Select
              value={tag || undefined}
              onChange={(value) => {
                if (!value.length) {
                  setTag(null);
                } else {
                  setTag(value);
                }
                setPageNum(1);
              }}
              allowClear
              mode="multiple"
              placeholder={i18n.t(LOCALS.please_select)}
              options={OMS_ORDER_TAG_OPTION_LIST}
              style={{ width: 150 }}
            />
          </Form.Item>

          {canFilterByStaffName && (
            <Form.Item label={<Trans i18nKey={LOCALS.staff} />}>
              <Select
                value={staffName || undefined}
                onChange={(value) => {
                  if (!value) {
                    setStaffName(null);
                  } else {
                    setStaffName(value);
                  }
                  setPageNum(1);
                }}
                allowClear
                showSearch
                placeholder={i18n.t(LOCALS.please_select)}
                options={staffNameOptions}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                style={{ minWidth: 150 }}
              />
            </Form.Item>
          )}

          <Form.Item label={<Trans i18nKey={LOCALS.freight} />}>
            <Select
              value={freightGtZero ? 1 : undefined}
              onChange={(value) => {
                setFreightGtZero(value === 1 ? true : null);
                setPageNum(1);
              }}
              allowClear
              placeholder={i18n.t(LOCALS.please_select)}
              options={[{ label: '大于 0', value: 1 }]}
              style={{ width: 150 }}
            />
          </Form.Item>

          <Form.Item label={<Trans i18nKey={LOCALS.remark} />}>
            <Input.TextArea
              value={noteInput}
              onChange={(e) => {
                setNoteInput(e.target.value);
              }}
              placeholder={i18n.t(LOCALS.please_enter) || ''}
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit">
              <Trans i18nKey={LOCALS.search} />
            </Button>
          </Form.Item>

          <Form.Item>
            <Button
              htmlType="button"
              onClick={() => {
                setOrderSnInput('');
                setProductSnInput('');
                setNoteInput('');
                setOrderSn(null);
                setProductSn(null);
                setCurrencyList(null);
                setCreatedFroms(null);
                setDeliveryType(null);
                setTag(null);
                setStatuses(null);
                setCreateTime(null);
                setStaffName(null);
                setFreightGtZero(null);
                setNote(null);
                setPageNum(1);
                setPageSize(10);
              }}
            >
              <Trans i18nKey={LOCALS.reset} />
            </Button>
          </Form.Item>

          <Form.Item>
            <Button
              disabled={total > 1000}
              onClick={async () => {
                // 获取数据
                const res = await getDataSource(1, 1000);
                const list = res?.list || [];

                // 获取支付账户列表
                const paymentAccountList =
                  await financialManagementAccountList();

                // 定义表头
                const headers = [
                  '受注番号',
                  '商品番号',
                  i18n.t(LOCALS.SYLYoWTeQq),
                  '買取金額',
                  '店頭金額',
                  '適用金額',
                  i18n.t(LOCALS.email),
                  i18n.t(LOCALS.order_type),
                  '税込販売額',
                  '税抜販売額',
                  i18n.t(LOCALS.tax_amount),
                  i18n.t(LOCALS.created_from),
                  i18n.t(LOCALS.staff),
                  i18n.t(LOCALS.remark),
                  i18n.t(LOCALS.order_status),
                  '受注日',
                  '売却日',
                ];

                // 转换数据
                const mappedData = list.map(
                  ({
                    id,
                    orderItems,
                    memberUsername,
                    payAmount,
                    payAmountActualCurrency,
                    createdFrom,
                    createTime,
                    paymentTime,
                    status,
                    totalTaxAmount,
                    orderPayList,
                    orderType,
                    staffName: orderStaffName,
                    note: orderNote,
                  }) => {
                    // 获取支付金额显示
                    let payAmountDisplay = `JPY ${payAmount.toLocaleString()}`;
                    if (
                      orderItems[0]?.actualCurrency &&
                      payAmountActualCurrency !== null
                    ) {
                      payAmountDisplay = `${
                        orderItems[0].actualCurrency
                      } ${payAmountActualCurrency.toLocaleString()}`;
                    }

                    let payAmountWithoutTax = payAmount - (totalTaxAmount || 0);
                    if (
                      orderItems[0]?.actualCurrency &&
                      payAmountActualCurrency !== null
                    ) {
                      payAmountWithoutTax =
                        payAmountActualCurrency - (totalTaxAmount || 0);
                    }

                    return {
                      受注番号: id,
                      商品番号: orderItems.map((i) => i.productId).join('\n'),
                      [i18n.t(LOCALS.SYLYoWTeQq)]: orderItems
                        .map((i) => {
                          return i.product.description;
                        })
                        .join('\n'),
                      買取金額: orderItems
                        .map((i) => {
                          const { costPrice } = i.product;

                          if (!costPrice) return '-';
                          return priceToWithoutTax(costPrice).toLocaleString(
                            'en-US',
                          );
                        })
                        .join('\n'),
                      店頭金額: orderItems
                        .map((i) =>
                          priceToWithoutTax(i.product.price).toLocaleString(
                            'en-US',
                          ),
                        )
                        .join('\n'),
                      適用金額: orderPayList
                        .filter(
                          (i) => i.payStatus === ORDER_PAYMENT_STATUS_MAP.PAID,
                        )
                        .map((i) => {
                          return `${
                            paymentAccountList.find(
                              (d) => d.accountCode === i.payType,
                            )?.accountName || i.payType
                          }（${i.payAmount.toLocaleString('en-US')}）`;
                        })
                        .join('\n'),
                      [i18n.t(LOCALS.email)]: memberUsername || '',
                      [i18n.t(LOCALS.order_type)]: findLabelByValue(
                        orderType,
                        orderTypeOptions,
                      ),
                      税込販売額: payAmountDisplay,
                      税抜販売額: `${
                        orderItems[0]?.actualCurrency || CURRENCY_ENUM.JPY
                      }  ${payAmountWithoutTax.toLocaleString('en-US')}`,
                      [i18n.t(LOCALS.created_from)]:
                        getOrderCreateSourceConfig(createdFrom).label,
                      [i18n.t(LOCALS.staff)]: orderStaffName || '-',
                      [i18n.t(LOCALS.remark)]: orderNote || '-',
                      [i18n.t(LOCALS.order_status)]: findLabelByValue(
                        status,
                        orderStatusOptions,
                      ),
                      [i18n.t(LOCALS.tax_amount)]:
                        totalTaxAmount?.toLocaleString('en-US') || '',
                      受注日: dayjs(createTime).format('YYYY-MM-DD'),
                      売却日: dayjs(paymentTime || createTime).format(
                        'YYYY-MM-DD',
                      ),
                    };
                  },
                );

                // 创建工作表
                const worksheet = XLSX.utils.json_to_sheet(mappedData, {
                  header: headers,
                });

                // 创建工作簿
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(
                  workbook,
                  worksheet,
                  'order export',
                );

                // 下载
                XLSX.writeFile(
                  workbook,
                  `注文リスト-${dayjs().format('YYYY-MM-DD')}.xlsx`,
                );
              }}
            >
              {i18n.t('download_excel')}（{total}）
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card bodyStyle={{ padding: '16px' }}>
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-4">
          {[
            {
              status: ORDER_STATUS_MAP.TO_BE_PAID,
              icon: Clock,
            },
            {
              status: ORDER_STATUS_MAP.TO_BE_DELIVERED,
              icon: Package,
            },
            {
              status: ORDER_STATUS_MAP.DELIVERED,
              icon: CheckCircle,
            },
            {
              status: ORDER_STATUS_MAP.COMPLETED,
              icon: CheckCircle,
            },
            {
              status: ORDER_STATUS_MAP.REFUND_ORDER,
              icon: AlertCircle,
            },
            {
              status: ORDER_STATUS_MAP.CANCLED,
              icon: XCircle,
            },
          ].map((status) => {
            const isChecked = statuses?.includes(status.status);
            const color = getOrderStatusConfig(status.status).color;

            return (
              <div
                key={status.status}
                className={classNames(
                  'text-center py-1.5 cursor-pointer rounded flex items-center justify-center gap-2 border transition-colors',
                  isChecked
                    ? 'bg-black text-white'
                    : 'bg-white hover:bg-gray-100',
                )}
                style={{
                  borderColor: isChecked ? 'white' : color,
                }}
                onClick={() => {
                  if (isChecked) {
                    const newStatuses = statuses?.filter(
                      (i) => i !== status.status,
                    );
                    setStatuses(
                      newStatuses && newStatuses.length ? newStatuses : null,
                    );
                  } else {
                    setStatuses(
                      statuses && statuses.length
                        ? [...statuses, status.status]
                        : [status.status],
                    );
                  }
                  setPageNum(1);
                }}
              >
                <span
                  className="font-semibold text-sm mr-1"
                  style={{ color: isChecked ? 'white' : color }}
                >
                  {findLabelByValue(status.status, orderStatusOptions)}(
                  {orderStatusStatistics.find((i) => i.status === status.status)
                    ?.count || 0}
                  )
                </span>
                {<status.icon className="w-4 h-4" style={{ color }} />}
              </div>
            );
          })}
        </div>
        <div className="mb-4 flex items-center gap-3">
          <Button
            disabled={!selectedPrintableOrderIds.length}
            onClick={() => {
              if (!selectedPrintableOrderIds.length) {
                message.warning('请选择已发货的可打印发票订单');
                return;
              }

              window.open(
                `/prints/order-invoice-v2?orderIdList=${selectedPrintableOrderIds.join(
                  ',',
                )}`,
              );
            }}
          >
            {i18n.t('print_sg_order_invoice')}
            {selectedPrintableOrderIds.length
              ? `（${selectedPrintableOrderIds.length}）`
              : ''}
          </Button>
        </div>
        <Table
          bordered
          tableLayout="auto"
          pagination={{
            showTotal,
            total,
            pageSize,
            current: pageNum,
            onChange: (page, pageSize) => {
              setPageNum(page);
              setPageSize(pageSize);
            },
          }}
          loading={loading || isUserInfoLoading}
          rowKey={'id'}
          scroll={{ x: 'max-content' }}
          rowSelection={{
            selectedRowKeys: selectedOrderIds,
            onChange: (selectedRowKeys, selectedRows) => {
              setSelectedOrderIds(selectedRowKeys);
              setSelectedOrders(selectedRows);
            },
            getCheckboxProps: (record) => ({
              disabled: !isPrintableInvoiceOrder(record),
            }),
          }}
          dataSource={dataSource}
          columns={columns}
        />
      </Card>
    </div>
  );
};

export default OrderList;
